import React from 'react';
import classNames from 'classnames';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';

import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';

import { formatUnits } from 'services/base/format';

import { randomString, randomDate } from 'services/base/random';
import { BasePathItems, BasePath, pagePathToPathString } from 'services/base/page_path';

import { Pangolin } from 'services/accounts/types';

import { IPangolinDomain, History } from 'services/pangolin/types';
import { HostDeviceType } from 'services/pangolin/protos/hosts_pb';
import { hostDisplayName, hostDisplayDeviceType } from 'services/pangolin/hosts_service';

import {
  fromBoolean,
  getShortAction,
  getMicroAction,
} from 'assets/enums/actions/actions';

import {
  fromHistoryDirection,
  getShortDirection,
  getMicroDirection,
} from 'assets/enums/directions/directions';

import {
  fromTransportProtocolNumber,
  getShortTransportProtocol,
} from 'assets/enums/protocols/protocols';

import {
  themedDeviceTypeIconClassStyles,
  getDeviceTypeIcon,
} from 'assets/enums/device_types/device_types';

import baseStyles from './history_item.module.css';
import 'flag-icons/css/flag-icons.min.css';

type HistoryItemStyles = {
  container: string;
  field: string;
  space: string;
  hostIcon: string;
  hostName: string;
  hostInfo: string;
  hostPort: string;
  details: string;
  detailsIcon: string;
  filler: string;
  deviceIcon: string;
  deviceIconFiller: string;
};

const themedStyles: ThemedStyles<HistoryItemStyles> = {
  [Theme.DAYLIGHT]: {
    container: baseStyles.daylightContainer,
    field: baseStyles.field,
    space: baseStyles.space,
    hostIcon: baseStyles.hostIcon,
    hostName: baseStyles.hostName,
    hostInfo: baseStyles.hostInfo,
    hostPort: baseStyles.hostPort,
    details: baseStyles.details,
    detailsIcon: baseStyles.detailsIcon,
    filler: baseStyles.daylightFiller,
    deviceIcon: baseStyles.deviceIcon,
    deviceIconFiller: baseStyles.daylightDeviceIconFiller,
  },
  [Theme.MIDNIGHT]: {
    container: baseStyles.midnightContainer,
    field: baseStyles.field,
    space: baseStyles.space,
    hostIcon: baseStyles.hostIcon,
    hostName: baseStyles.hostName,
    hostInfo: baseStyles.hostInfo,
    hostPort: baseStyles.hostPort,
    details: baseStyles.details,
    detailsIcon: baseStyles.detailsIcon,
    filler: baseStyles.midnightFiller,
    deviceIcon: baseStyles.deviceIcon,
    deviceIconFiller: baseStyles.midnightDeviceIconFiller,
  },
};

type HistoryProgressProps = {
  locale: string;
  format: object;
};
export const HistoryProgress = observer(({ locale, format }: HistoryProgressProps) => {
  const styles = useStyles(themedStyles);

  const placeholder_name_src: string = randomString(15, 20);
  const placeholder_info_src: string = randomString(10, 20);
  const placeholder_name_dst: string = randomString(15, 20);
  const placeholder_info_dst: string = randomString(10, 20);
  const placeholder_direction: string = randomString(6, 8);
  const placeholder_protocol: string = randomString(4, 4);
  const placeholder_bytes: string = randomString(5, 7);
  const placeholder_packets: string = randomString(6, 9);
  const placeholder_action: string = randomString(8, 8);
  const placeholder_date: Date = randomDate(new Date(2000), new Date());

  return (
    <div className={styles.container}>
      <div className={styles.field}>
        <div className={styles.hostIcon}>
          <span className={styles.deviceIconFiller}/>
        </div>
        <div className={styles.hostName}>
          <div className={styles.filler}>
            {placeholder_name_src}
          </div>
          <div className={styles.hostInfo}>
            <div className={styles.filler}>
              {placeholder_info_src}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.field}>
        <div className={styles.hostIcon}>
          <span className={styles.deviceIconFiller}/>
        </div>
        <div className={styles.hostName}>
          <div className={styles.filler}>
            {placeholder_name_dst}
          </div>
          <div className={styles.hostInfo}>
            <div className={styles.filler}>
              {placeholder_info_dst}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.space}>
      </div>
      <div className={styles.field}>
        <div className={styles.details}>
          <div className={styles.filler}>{placeholder_direction}</div>
        </div>
        <div className={styles.details}>
          <div className={styles.filler}>{placeholder_protocol}</div>
          &nbsp;
          <div className={styles.filler}>{placeholder_bytes}</div>
          &nbsp;
          <div className={styles.filler}>{placeholder_packets}</div>
        </div>
      </div>
      <div className={styles.field}>
        <div className={styles.details}>
          <div className={styles.filler}>{placeholder_action}</div>
        </div>
        <div className={styles.details}>
          <div className={styles.filler}>
            {placeholder_date.toLocaleString(locale, format)}
          </div>
        </div>
      </div>
    </div>
  );
});

type HistoryItemProps = {
  domain: IPangolinDomain;
  pangolin: Pangolin;
  pair: [string, History];
  locale: string;
  format: object;
};
export const HistoryItem = observer(({ domain, pangolin, pair: [index, history], locale, format }: HistoryItemProps) => {
  const nav = useHistory();
  const styles = useStyles(themedStyles);
  const deviceIcons = useStyles(themedDeviceTypeIconClassStyles);

  const onShowModal = React.useCallback(action(() => {
    domain.historyItem = history;
    nav.push(pagePathToPathString({
      kind: BasePath.HISTORY_LIST,
      pangolinID: pangolin.id,
      itemsPath: {
        kind: BasePathItems.VIEW_ITEM,
        itemID: index,
      },
    }));
  }), []);

  const allowed = fromBoolean(history.allowed);
  const direction = fromHistoryDirection(history.direction);
  const transport = fromTransportProtocolNumber(history.proto_l4);

  const host_src = domain.hosts.getMac(history.mac_src);
  const host_dst = domain.hosts.getMac(history.mac_dst);

  const name_src = host_src ? (hostDisplayName(host_src) ?? host_src.mac ?? host_src.ip) :
                   (history.domains_src?.length ? history.domains_src[0] : history.addr_src);
  const name_dst = host_dst ? (hostDisplayName(host_dst) ?? host_dst.mac ?? host_dst.ip) :
                   (history.domains_dst?.length ? history.domains_dst[0] : history.addr_dst);

  const type_src = host_src ? hostDisplayDeviceType(host_src) : HostDeviceType.H_UNKNOWN;
  const type_dst = host_dst ? hostDisplayDeviceType(host_dst) : HostDeviceType.H_UNKNOWN;

  const flag_src = history.location_src?.country_short ?? 'XX';
  const flag_dst = history.location_dst?.country_short ?? 'XX';

  const icon_cls_src = getDeviceTypeIcon(deviceIcons, type_src);
  const icon_src = host_src ? <span className={classNames(styles.deviceIcon, icon_cls_src)}/> :
                   <span className={classNames('fi', `fi-${flag_src.toLowerCase()}`)}/>;
  const icon_cls_dst = getDeviceTypeIcon(deviceIcons, type_dst);
  const icon_dst = host_dst ? <span className={classNames(styles.deviceIcon, icon_cls_dst)}/> :
                   <span className={classNames('fi', `fi-${flag_dst.toLowerCase()}`)}/>;

  const date = new Date(0); date.setUTCSeconds(history.start);

  return (
    <div
      className={styles.container}
      onClick={onShowModal}>
      <div className={styles.field}>
        <div className={styles.hostIcon}>
          {icon_src}
        </div>
        <div className={styles.hostName}>
          {name_src}
          <div className={styles.hostInfo}>
            {history.addr_src}:{history.port_src}
          </div>
        </div>
      </div>
      <div className={styles.field}>
        <div className={styles.hostIcon}>
          {icon_dst}
        </div>
        <div className={styles.hostName}>
          {name_dst}
          <div className={styles.hostInfo}>
            {history.addr_dst}:{history.port_dst}
          </div>
        </div>
      </div>
      <div className={styles.space}>
      </div>
      <div className={styles.field}>
        <div className={styles.details}>
          <span className={styles.detailsIcon}>{getMicroDirection(direction)}</span>
          {getShortDirection(direction)}
        </div>
        <div className={styles.details}>
          {getShortTransportProtocol(transport)}
          &nbsp;/&nbsp;
          {formatUnits(history.bytes_orig + history.bytes_repl, 'B')}
          &nbsp;/&nbsp;
          {formatUnits(history.pkts_orig + history.pkts_repl, ' pkts')}
        </div>
      </div>
      <div className={styles.field}>
        <div className={styles.details}>
          <span className={styles.detailsIcon}>{getMicroAction(allowed)}</span>
          {getShortAction(allowed)}
        </div>
        <div className={styles.details}>
          {date.toLocaleString(locale, format)}
        </div>
      </div>
    </div>
  );
});
